import React, { useEffect, useCallback, useState } from "react";
import SideLayout from "../components/common/sidelayout";
import { useLocation } from "react-router-dom";
import Analytics from "./analytics/analytics";
import BasicAnalytics from "./basic_analytics/BasicAnalytics";
import UltraAdminAnalytics from "./UltraAdminAnalytics/UltraAdminAnalytics";
import Report from "../pages/report/report";
import Settings from "./settings/settings";
import Profile from "./profile/profile";
import "./dashboard.scss";
import moment from "moment";
import Cookie from "js-cookie";
import axios from "axios";
import urls from "../utils/url";
import { isJson } from "../utils/converter";
import AlertModal from "../components/alertModal/alertModal";
import { useLogo } from "../context/LogoContext";

const Dashboard = () => {
  const location = useLocation();
  const cookie = Cookie.get("access_token");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [alertData, setAlertData] = useState([]);
  const [endDate, setEndDate] = useState(moment().unix().toString());
  const [startDate, setStartDate] = useState(
    moment().subtract(4, "days").unix().toString(),
  );
  const [placeID, setPlaceID] = useState("");
  const [accountType, setAccountType] = useState("");
  const [lastDataRefresh, setLastDataRefresh] = useState(new Date()); // Track actual data refresh time
  // alert changes
  const [openAlertPanel, setOpenAlertPanel] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMrn, setIsMrn] = useState(true);

  const cookieData = Cookie.get("data");
  const { finalLogo, setLogo, setHospitalName } = useLogo();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    JSON.parse(sessionStorage.getItem("sidebarCollapsed")) || false,
  );

  const [unseenCount, setUnseenCount] = useState(0);

  const getLastSeenTime = () => {
    const currentUserId = getCurrentUserId();
    if (!currentUserId) return 0;

    const stored = localStorage.getItem(`alertsLastSeen_${currentUserId}`);
    if (!stored) return 0;

    const timestamp = parseInt(stored);
    // If timestamp is too high (13 digits), it's in milliseconds, convert to seconds
    if (timestamp > 9999999999) {
      const corrected = Math.floor(timestamp / 1000);
      localStorage.setItem(
        `alertsLastSeen_${currentUserId}`,
        corrected.toString(),
      );
      return corrected;
    }

    // Safety check: if timestamp is in the future, reset it
    const currentTime = moment().unix();
    if (timestamp > currentTime) {
      localStorage.removeItem(`alertsLastSeen_${currentUserId}`);
      return 0;
    }

    return timestamp;
  };

  const getCurrentUserId = () => {
    if (!cookieData) return null;
    const parsed = JSON.parse(cookieData);
    return (
      parsed.user_name ||
      parsed.user_id ||
      parsed.id ||
      parsed.email ||
      parsed.username
    );
  };
  // SCROLL BUTTON
  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 600);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    setStartDate(
      moment.unix(Number(endDate)).subtract(4, "days").unix().toString(),
    );
  }, [endDate]);

  const handleReload = () => {
    window.location.reload();
  };
  useEffect(() => {
    const handler = (e) => {
      setIsSidebarCollapsed(e.detail.isCollapsed);
    };

    window.addEventListener("sidebarStateChange", handler);
    return () => window.removeEventListener("sidebarStateChange", handler);
  }, []);

  useEffect(() => {
    if (cookieData) {
      const parsed = JSON.parse(cookieData);

      setAccountType(parsed.account_type);
      setIsMrn(parsed.fetal_distress_algorithm);

      const currentUserId =
        parsed.user_name ||
        parsed.user_id ||
        parsed.id ||
        parsed.email ||
        parsed.username;
      const storedUserId = localStorage.getItem("currentUserId");

      // Only store current user ID, don't clear other users' alert timestamps
      if (currentUserId && currentUserId !== storedUserId) {
        localStorage.setItem("currentUserId", currentUserId);
      }

      // store into global Context
      setLogo(parsed.logo || null);
      const hospitalDetail = JSON.parse(Cookie.get("hospital_detail") || "{}");
      setHospitalName(hospitalDetail.hospitalName || "");
    }
  }, []);

  useEffect(() => {
    if (!placeID || accountType !== "Premium") return;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchAlerts(placeID);
      }
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [placeID, accountType]);

  useEffect(() => {
    fetchPlaceIDs();

    // One-time fix for users with incorrect timestamp format
    const stored = localStorage.getItem("alertsLastSeen");
    if (stored && parseInt(stored) > 9999999999) {
      localStorage.removeItem("alertsLastSeen");
    }
  }, []);
  const fetchPlaceIDs = useCallback(async () => {
    setLoading(true);
    axios
      .get(`${urls.BETA_URL}website/places/`, {
        headers: {
          "Request-ID": "allow|me",
          "Content-Type": "application/json",
          Authorization: `Bearer ${cookie}`,
        },
      })
      .then((res) => {
        // console.log("🔍 RAW ALERT API RESPONSE:", res);
        // console.log("🔍 RAW DATA:", res.data);
        // console.log("🔍 JSON PARSED RESPONSE:", isJson(res.data));
        if (res.data) {
          const response = isJson(res.data).response;
          const ids = response.map((obj) => obj.id).toString();
          setPlaceID(ids);
          fetchAlerts(ids);
        }
        setLoading(false);

        // Notify parent about initial data load
        const loadTime = new Date();
        setLastDataRefresh(loadTime);
      })
      .catch(() => {
        setPlaceID("");
        setError(true);
        setLoading(false);
      });
  }, [cookie]);

  const fetchAlerts = (placeID) => {
    setLoading(true);
    const freshEndDate = moment().unix().toString();
    const freshStartDate = moment().subtract(4, "days").unix().toString();

    axios
      .get(
        `${urls.BETA_URL}nurse/patients/?place_ids=${placeID}&forms=alerts,basic_detail&from_time=${freshStartDate}&to_time=${freshEndDate}&`,
        {
          headers: {
            "Request-ID": "allow|me",
            "Content-Type": "application/json",
            Authorization: `Bearer ${cookie}`,
          },
        },
      )
      .then((res) => {
        if (res.data) {
          const response = isJson(res.data).response;

          const nonEmptyAlerts = response
            .map((obj) => {
              const filtered = obj.alerts;
              if (filtered && filtered.length > 0) {
                return {
                  live_alerts: filtered.map((a) => a.alert_message).join(", "),
                  patient_name: obj.basic_detail?.name,
                  patient_id: obj.id,
                  time: filtered[0]?.data_time,
                  mrn: obj.basic_detail?.mrn,
                };
              }
              return null;
            })
            .filter(Boolean);

          setAlertData(nonEmptyAlerts);

          const lastSeen = getLastSeenTime();
          const unseen = nonEmptyAlerts.filter((a) => a.time > lastSeen);
          setUnseenCount(unseen.length);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setAlertData([]);
        setLoading(false);
      });
  };

  //alert changes
  const togglePanel = () => {
    const isOpening = !openAlertPanel;
    setOpenAlertPanel(isOpening);

    if (isOpening) {
      const currentUserId = getCurrentUserId();
      if (currentUserId) {
        const currentTime = moment().unix();
        localStorage.setItem(
          `alertsLastSeen_${currentUserId}`,
          currentTime.toString(),
        );
        setUnseenCount(0);
      }
    }
  };

  // Handle data refresh from child components
  const handleDataRefresh = useCallback((refreshTime) => {
    setLastDataRefresh(refreshTime);
  }, []);

  return (
    <>
      <div
        className={openAlertPanel ? "blur-dashboard" : "dashboard"}
        style={{
          "--sidebar-width": isSidebarCollapsed ? "84px" : "260px",
          "--content-gap": isSidebarCollapsed ? "1rem" : "2rem",
          "--header-offset": isSidebarCollapsed ? "40px" : "0px",
        }}
      >
        <SideLayout />

        <div className="main-layout">
          {/* ------------------------ HEADER ---------------------------- */}
          <div className="dashboard-header">
            {/* LEFT SIDE → LAST UPDATE */}
            <div className="update-left">
              <img
                src="/update.svg"
                width={18}
                height={18}
                alt="update"
                onClick={handleReload}
                style={{ cursor: "pointer", marginRight: "10px" }}
              />
              <span className="update-text">
                Last Update:{" "}
                {moment(lastDataRefresh).format("DD MMM YYYY hh:mm A")}
              </span>
            </div>

            {/* Patient Reports portals its search bar in here when the
                logged-in place is in GRAPH_PLACE_IDS — see report.jsx */}
            <div id="report-search-slot" className="report-search-slot"></div>

            {/* RIGHT SIDE → LOGO OR FALLBACK */}
            <div className="logo-right">
              {finalLogo.isImage ? (
                <img
                  src={finalLogo.value}
                  alt="Hospital Logo"
                  className="hospital-logo"
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <div
                  className="hospital-logo-fallback"
                  style={{
                    color: "#60327E",
                    minWidth: "120px",
                    textAlign: "right",
                    whiteSpace: "pre-line",
                    fontWeight: "700",
                    lineHeight: "1.2",
                  }}
                >
                  {finalLogo.value || "Hospital\nName"}
                </div>
              )}

              {accountType === "Premium" && (
                <div className="notification-bell" onClick={togglePanel}>
                  <img
                    src="/alerts_notifications.svg"
                    width={40}
                    height={40}
                    alt="bell"
                  />
                  {unseenCount > 0 && (
                    <div className="notification-badge">{unseenCount}</div>
                  )}
                </div>
              )}
            </div>
          </div>
          {/* ------------------------ END HEADER ---------------------------- */}

          <div className="component">
            {location.pathname === "/dashboard/analytics" && <Analytics />}
            {location.pathname === "/dashboard/basic-analytics" &&
              (() => {
                const isUltraAdmin =
                  JSON.parse(Cookie.get("data") || "{}").is_ultra_admin ===
                  true;
                return isUltraAdmin ? (
                  <UltraAdminAnalytics />
                ) : (
                  <BasicAnalytics />
                );
              })()}
            {location.pathname === "/dashboard/report" && (
              <Report isMrn={isMrn} onDataRefresh={handleDataRefresh} />
            )}
            {location.pathname === "/settings" && <Settings />}
            {location.pathname === "/dashboard/profile" && <Profile />}

            <div
              className={`back-to-top ${
                isVisible ? "show-back-to-top" : "hide-back-to-top"
              }`}
              onClick={scrollToTop}
            >
              <img src="/arrow_upward.svg" alt="" />
              <p className="back-to-top-text">Back To Top</p>
            </div>
          </div>
        </div>
      </div>

      {openAlertPanel && (
        <AlertModal
          alertData={alertData}
          onClose={togglePanel}
          openAlertPanel={openAlertPanel}
          isMrn={isMrn}
        />
      )}
    </>
  );
};

export default Dashboard;
